import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Link, browserHistory } from 'react-router';
import { animateScroll } from 'react-scroll';
import TileGrid from 'components/TileGrid';
import { SHOW_DETAIL_RELATED_THUMBNAIL_IMAGE, renderImage } from 'helpers/ImageHelper';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import { clipsVideoProcessor } from 'helpers/VideoProcessorHelper';
import { PAGE_TYPE_CLIPS, slugify } from 'helpers/ContentHelper';
import CarouselSection from 'screens/ContentDetail/components/CarouselSection';
import { SERIES_CLIP_TILE } from 'components/TileV2';
import Icon from 'components/Icon';
import Loader from 'components/Loader';

import {
  PANEL_TYPE_CLIPS,
  PANEL_TYPE_SCREEN_PANEL,
  getTabsOfMultipleTypes,
  getTabName,
  getTabDataUrl,
  getSelectedItem,
} from 'screens/SeriesDetail/SeriesDetailHelpers';
import { helperGetApiHeaders, helperConvertApiRoute } from 'helpers/ApiHelper';
import Style from './Clips.module.scss';
import TileGridStyle from './ClipsTileGrid.module.scss';
import TileStyle from './ClipsTile.module.scss';
import ClipsDropdown from './ClipsDropdown';
import { fetchNextClipsPage, setNextClipsPage, setFetchingStatus } from './ClipsActions';

import Divider from '../Divider';

const Clips = ({
  accessToken,
  breakpoint,
  deviceId,
  fetchPanelData,
  inline,
  isAnonymous,
  pageType,
  panels,
  sections,
  seriesName,
  seriesType,
  showCode,
}) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const dispatch = useDispatch();
  const { paginationItems, nextPage, fetching } = useSelector(state => state.clips);

  const clipsSections = getTabsOfMultipleTypes(sections, [
    PANEL_TYPE_CLIPS,
    PANEL_TYPE_SCREEN_PANEL,
  ]).map(section => {
    const { id } = section;
    const name = getTabName({ tab: section, seriesType });
    return { ...section, name, showCode, id, url: id };
  });
  const selectedItem = getSelectedItem(clipsSections);
  const nextPageUrl = _.get(selectedItem, 'items.view.next');
  const totalItems = _.get(selectedItem, 'items.totalItems');
  const isClipsPage = pageType === 'clips';
  useEffect(() => {
    dispatch(setNextClipsPage(nextPageUrl));
  }, [dispatch, nextPageUrl]);
  if (_.isEmpty(sections)) return null;

  const onClipsSelect = item => {
    if (!item || !item.showCode || !item.url) return;
    const { showCode, name } = item;
    const slug = slugify(name);

    setOpenDropdown(false);
    const newClipsNextPage = _.get(item, 'items.view.next');
    dispatch(setNextClipsPage(newClipsNextPage));
    const current = window.location.href;
    const isClipsUrl = current.indexOf(`/${showCode}/clips/`) !== -1;
    const clipsPath = isClipsUrl ? `clips/${slug}` : slug;
    const path = `${window.location.origin}/${showCode}/${clipsPath}/`;
    if (current === path) return;
    browserHistory.replace(path);
  };

  const onDropdownOpen = () => {
    if (openDropdown) {
      setOpenDropdown(false);
    } else {
      setOpenDropdown(true);
    }
  };

  const clipsDropdownProps = {
    clipsSections,
    isClipsPage,
    onDropdownOpen,
    onSelect: onClipsSelect,
    openDropdown,
  };
  const renderDropdown = () => <ClipsDropdown {...clipsDropdownProps} />;

  const renderCarousel = () => {
    const panelProps = {
      breakpoint,
      inline,
      seriesType,
    };
    const section = getSelectedItem(clipsSections);
    const panelUrl = getTabDataUrl(section);
    const panelId = _.get(section, '@id', panelUrl);

    let panelItems = section;
    if (!panelItems && panels && panels[panelId]) {
      panelItems = panels[panelId].panelData;
    }

    const sliderMax = 12;
    if (panelItems) {
      const member = panelItems?.items?.member?.slice(0, sliderMax);
      const items = panelItems.items ? { ...panelItems.items, member } : {};
      panelItems = !_.isEmpty(panelItems) ? { ...panelItems, items } : {};
    }

    const totalItems = _.get(panelItems, 'items.totalItems', 0);
    const { tileGrid_seeAllButtonText: seeAllText } = window.Language;
    let seeAll = null;
    const slug = slugify(section.name);

    if (totalItems > sliderMax) {
      seeAll = (
        <Link
          className={Style.seeAllButton}
          to={`/${showCode}/clips/${slug}/`}
          onClick={() => {
            animateScroll.scrollToTop({ duration: 500 });
            handleAnalytics({
              event: Events.DETAILS_PAGE_PLAYLIST_LANDING,
              payload: {
                pageName: `detail:${seriesType}:${seriesName}:playlist-landing`,
                pageType: `delta:detail:playlist-landing`,
              },
            });
          }}
        >
          <span>{seeAllText}</span>
          <Icon color="white" size="small" viewBox="0 0 18 32" icon="ios-arrow-right" />
        </Link>
      );
    }

    const shouldFetch = !panelItems && section;
    if (shouldFetch) {
      const { '@id': id } = section;
      const url = panelUrl ? helperConvertApiRoute(panelUrl) : helperConvertApiRoute(id);
      const headers = helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId });
      fetchPanelData(url, headers, panelId);
    }

    if (panelItems) {
      return (
        <CarouselSection
          isAnonymous={isAnonymous}
          isPosters={false}
          isSeasons
          tileType={SERIES_CLIP_TILE}
          panel={panelItems}
          {...panelProps}
          dropdownHeadline={renderDropdown()}
          seeAll={seeAll}
        />
      );
    }
    if (shouldFetch) {
      return (
        <div className={Style.loaderContainer}>
          <Loader />
        </div>
      );
    }
    return null;
  };

  const renderClipsPage = () => {
    const returnButton = (
      <div className={Style.returnButtonContainer}>
        <Link className={Style.returnButton} to={`/${showCode}/`}>
          <Icon color="white" size="medium" icon="ios-arrow-left" />
          {`Back to ${seriesName}`}
        </Link>
      </div>
    );
    const section = getSelectedItem(clipsSections);

    const items = _.get(section, 'items.member', []);

    const processor = item => {
      const processedItem = clipsVideoProcessor(item) || {};
      const description = _.get(item, 'item.description');
      const data = {
        ...processedItem,
        description,
      };
      return data;
    };

    const gridType = 'grid-4-2-1';
    const tileGridProps = {
      context: 'seriesDetail',
      items: [...(items || []), ...(paginationItems || [])],
      isSeriesDetailPage: true,
      preventLazyLoad: true,
      processor,
      gridType,
      renderImage: image =>
        renderImage({
          breakpoint,
          imageType: SHOW_DETAIL_RELATED_THUMBNAIL_IMAGE,
          ...(image || {}),
        }),
      tileDetailToggle: false,
      tileTheme: TileStyle,
      theme: TileGridStyle,
      shouldDisplayNetworkLogo: false,
      usePlayButtonThin: true,
    };

    const SeeMoreButton = () => {
      if (fetching) return <Loader />;
      const { tileGrid_seeMoreButtonText: seeMoreLabel } = window.Language;
      const numClips = totalItems - [...items, ...paginationItems].length;
      const buttonLabel = `${seeMoreLabel} (${numClips})`;
      return (
        <div className={Style.seeMoreContainer}>
          <button
            className={Style.seeMoreButton}
            type="button"
            onClick={() => {
              dispatch(setFetchingStatus(true));
              dispatch(
                fetchNextClipsPage({
                  accessToken,
                  deviceId,
                  pageType,
                  url: nextPage,
                }),
              );
            }}
          >
            {buttonLabel}
          </button>
        </div>
      );
    };

    return (
      <>
        {returnButton}
        {renderDropdown()}
        <TileGrid {...tileGridProps} />
        {nextPage && <SeeMoreButton />}
      </>
    );
  };

  if (pageType === PAGE_TYPE_CLIPS) {
    return <div className={Style.clipsPage}>{renderClipsPage()}</div>;
  }

  return (
    <>
      <section className={Style.clipsCarousel}>
        <div>{renderCarousel()}</div>
      </section>
      <Divider />
    </>
  );
};

Clips.propTypes = {
  accessToken: PropTypes.string,
  breakpoint: PropTypes.string.isRequired,
  deviceId: PropTypes.string,
  fetchPanelData: PropTypes.func.isRequired,
  inline: PropTypes.shape({}),
  isAnonymous: PropTypes.bool,
  pageType: PropTypes.string,
  panels: PropTypes.shape({}).isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape([])).isRequired,
  seriesName: PropTypes.string.isRequired,
  seriesType: PropTypes.string.isRequired,
  showCode: PropTypes.string.isRequired,
};

export default React.memo(Clips);
